import { api } from "@/comn";
import { comnUtils } from "@/comn/utils";
import { colEnvs, colUtils } from "@/tz/go/egaz/osci/colt/comn"; // 시스템 공통 유틸
import { TFormSchema, TGridSchema } from "@/comn/hooks";
import { lpcoEnvs } from "@/tz/go/egaz/osci/lpco/comn";
import { format } from "path";

/*
 * Bill Management Service
 * !== 고지 관리 서비스 ==!
 */

/*
 * @ BASE
 * @ 서비스에서 사용하는 기본 정보 세팅
 * @ path : 화면들의 기본 웹 경로
 * @ api : 사용하는 api 의 기본 주소
 * @ api 주소가 여러개일 경우 _ 로구분하여 의미에 맞게 카멜표기업으로 표시 : api_clr
 * @ nodes : 화면의 Page.Navigation 에서 사용하는 기본 경로 Array
 */
export const BASE = {
    path: `${lpcoEnvs.base}/colt/ntfc`,
    api: `${process.env.REACT_APP_API_OSCI}/api/v1/osci/colt/intf/ntfc`,
    api_v1: `${process.env.REACT_APP_API_OSCI}/api/v1/osci/lpco/fee`,
    nodes: [
        { path: "/", label: "Collection" },
        { path: "/colt", label: "Notice" },
        { path: "/colt/ntfc", label: "Notice" },
    ],
};

/*
 * @ URLS
 * @ 화면에서 사용하는 주소들 정의
 * @ 해당화면의 컴포넌트명 대문자로 정의
 */
export const URLS = {
    osci0402001q: `/osci/colt/rcpt/osci0402001q`, // Receipt List !== 수납 목록 ==!
    osci0402003q: `/osci/colt/rcpt/osci0402003q`, // Non-Receipt List !== 미수납 목록 ==!
    osci0402004q: `/osci/colt/rcpt/osci0402004q`, // Receipt Error List !== 수납 오류 목록 ==!
    osci0401001q: `/osci/colt/ntfc/osci0401001q`, // Bill List !== 고지 목록 ==!
    osci0401002q: `/osci/colt/ntfc/osci0401002q`, // Bill Detail !== 고지 상세 ==!
    osci0401003q: `/osci/colt/ntfc/osci0401003q`, // TANSAD Bill List !== TANSAD 고지 목록 ==!
    osci0401004q: `/osci/colt/ntfc/osci0401004q`, // Deadline Extension List !== 데드라인 연장 목록 ==!
    osci0401005q: `/osci/colt/ntfc/osci0401005q`, // Deadline Extension Detail !== 데드라인 연장 상세 ==!
    osci0401006s: `/osci/colt/ntfc/osci0401006s`, // Deadline Extension Registration !== 데드라인 연장 등록 ==!
};

/*
 * @ APIS
 * @ 화면에서 사용하는 api 정의
 */
export const APIS = {
    // Get Bill List !== 고지 목록 조회 ==!
    getBllList: (data: any, page: number, size: number) => {
        return api.get(`${BASE.api}/bllLst?page=${page}&size=${size}`, {
            params: comnUtils.toGetParams(data),
        });
    },
    // Get Bill Detail !== 고지 상세 ==!
    getBll: (id: any) => {
        return api.get(`${BASE.api}/${id}/bllDtl`);
    },
    // Get Bill Detail Menu !== 고지 상세 메뉴 ==!
    getBllMenu: (id: any) => {
        return api.get(`${BASE.api}/bllDtlMenu`);
    },
    // Get TANSAD Info !== TANSAD 정보 조회 ==!
    getTansad: (id: any) => {
        return api.get(`${BASE.api}/${id}/tansad`);
    },
    // Get TANSAD Bill List(detail) !== TANSAD 고지 목록 조회 ==!
    getBllListByTansad: (id: any, page: number, size: number) => {
        return api.get(`${BASE.api}/${id}/tansadBllLst?page=${page}&size=${size}`);
    },
    // Get Deadline Extension List !== 데드라인 연장 목록 조회 ==!
    getDdlnXtnsList: (data: any, page: number, size: number) => {
        return api.get(`${BASE.api}/xtnsLst?page=${page}&size=${size}`, {
            params: comnUtils.toGetParams(data),
        });
    },
    // Get Deadline Extension Detail !== 데드라인 연장 상세 ==!
    getDdlnXtns: (id: any) => {
        return api.get(`${BASE.api}/${id}/xtnsDtl`);
    },
    // Delete Deadline Extension !== 데드라인 연장 삭제 (단건, 특정 회차) ==!
    deleteDdlnXtns: (bllMgmtId: any, ddlnXtnsDgr?: any) => {
        return api.delete(`${BASE.api}/${bllMgmtId}/xtnsDel`, { params: { ddlnXtnsDgr } });
    },
    // Save Deadline Extension !== 데드라인 연장 신청 저장 ==!
    saveDdlnXtns: (data: any) => {
        return api.post(`${BASE.api}/xtnsSave`, {
            ...data,
        });
    },
    // Deadline Extension Approval Request After !== 데드라인 연장 승인 요청 후 처리 ==!
    ddlnXtnsAprvRqstAf: (data: any) => {
        return api.post(`${BASE.api}/ddlnXtnsAprvRqstAf`, {
            ...data,
        });
    },
    updtBll: (data: any) => {
        return api.post(`${BASE.api_v1}/updtBll`, {
            ...data,
        });
    },
};

/*
 * @ 그리드 스키마 정의
 * @ 화면에서 사용하는 그리드 구조 정의
 * @ SG_ 로 시작 (Schema grid)
 * @ SG_[그리드명과 조회하는 데이터 이름 메타 단어(용어) 대문자, "_" 로 구분 , 그리드명 맨 뒤에 "_LIST" 로 종료]
 */

// Schema of Bill List Grid !== 고지 목록 그리드 스키마 ==!
export const SG_BLL_LIST: TGridSchema = {
    id: "grid_bllList",
    options: {
        radio: false, // 라디오 버튼 보이기/숨기기 , default : false
        checkbox: false, //체크박스 보이기 숨기기, default : false
        pagination: "out", // 그리드 데이터 페이징 "out" : 데이터를 외부에서 페이징해서 가져오는 경우 , "in" : 데이터를 전체 가져와서 그리드 내부에서 페이징 및 편집
        index: true, // 그리드 번호  index : true ( asc )  , index : "DESC" ( desc 역순 )
        edit: false, // 그리드 편집 가능 여부 , default : false
        height: 500,
    },
    head: [
        { cells: [{ header: "L_BLL_NO", binding: "bllNo", width: 180 }] },
        { cells: [{ header: "L_BLL_DT", binding: "bllDt", width: 100 }] },
        { cells: [{ header: "L_CTRL_NO", binding: "ctrlNo", width: "*" }] },
        { cells: [{ header: "L_TANSAD_NO", binding: "tansadNo", width: 130 }] },
        { cells: [{ header: "L_TANSAD_DT", binding: "tansadDt", width: 100 }] },
        { cells: [{ header: "L_TXPR", binding: "pyerMgmtNo", width: 130 }] },
        { cells: [{ header: "L_BLL_AMT", binding: "feeAmt", width: "*" }] },
        { cells: [{ header: "L_RCPT_AMT", binding: "rcptAmt", width: "*" }] },
        { cells: [{ header: "L_DUE_DT", binding: "lastPayDdlnDt", width: 100 }] },
        { cells: [{ header: "L_BLL_MGMT_ID", binding: "bllMgmtId", width: 180 }] },
        { cells: [{ header: "L_CNCL_YN", binding: "dspoYn", width: 80 }] },
        { cells: [{ header: "L_BLL_TP", binding: "bllTpCd", width: "*" }] },
    ],
    body: [
        { cells: [{ binding: "bllNo" }] },
        { cells: [{ binding: "bllDt", type: "date" }] },
        { cells: [{ binding: "ctrlNo" }] },
        { cells: [{ binding: "tansadNo" }] },
        { cells: [{ binding: "tansadDt", type: "date" }] },
        { cells: [{ binding: "pyerMgmtNo" }] },
        { cells: [{ binding: "feeAmt", type: "number", thousandSeparator: true, align: "right" }] },
        { cells: [{ binding: "rcptAmt", type: "number", thousandSeparator: true, align: "right" }] },
        { cells: [{ binding: "lastPayDdlnDt", type: "date" }] },
        { cells: [{ binding: "bllMgmtId" }] },
        { cells: [{ binding: "dspoYn" }] },
        { cells: [{ binding: "bllTpCd", area: "comnCd", comnCd: "OSC0166", type: "code" }] },
    ],
};

// Schema of TANSAD Bill List Grid !== TANSAD 고지 목록 그리드 스키마 ==!
export const SG_TANSAD_BLL_LIST: TGridSchema = {
    id: "grid_tansadBllList",
    options: {
        index: true, // 그리드 번호  index : true ( asc )  , index : "DESC" ( desc 역순 )
        edit: false, // 그리드 편집 가능 여부 , default : false
        height: "auto",
    },
    head: [
        { cells: [{ header: "L_BLL_NO", binding: "bllNo", width: 180 }] },
        { cells: [{ header: "L_BLL_DT", binding: "bllDt", width: 100 }] },
        { cells: [{ header: "L_CTRL_NO", binding: "ctrlNo", width: "*" }] },
        { cells: [{ header: "L_DUE_DT", binding: "lastPayDdlnDt", width: 100 }] },
        { cells: [{ header: "L_FEE_AMT", binding: "feeAmt", width: "*" }] },
        { cells: [{ header: "L_CNCL_YN", binding: "dspoYn", width: 100 }] },
        { cells: [{ header: "L_BLL_TP", binding: "bllTpCd", width: 150 }] },
        { cells: [{ header: "L_BLL_STAT", binding: "bllStatCd", width: 180 }] },
    ],
    body: [
        { cells: [{ binding: "bllNo" }] },
        { cells: [{ binding: "bllDt", type: "date" }] },
        { cells: [{ binding: "ctrlNo" }] },
        { cells: [{ binding: "lastPayDdlnDt", type: "date" }] },
        { cells: [{ binding: "feeAmt", type: "number", thousandSeparator: true, align: "right" }] },
        { cells: [{ binding: "dspoYn" }] },
        { cells: [{ binding: "bllTpCd", area: "comnCd", comnCd: "OSC0166", type: "code" }] },
        { cells: [{ binding: "bllStatCd", area: "comnCd", comnCd: "OSC0056", type: "code" }] },
    ],
};

// Schema of Deadline Extension List Grid !== 데드라인 연장 목록 그리드 스키마 ==!
export const SG_DDLN_XTNS_LIST: TGridSchema = {
    id: "grid_ddlnXtnsList",
    options: {
        radio: false, // 라디오 버튼 보이기/숨기기 , default : false
        checkbox: false, //체크박스 보이기 숨기기, default : false
        pagination: "out", // 그리드 데이터 페이징 "out" : 데이터를 외부에서 페이징해서 가져오는 경우 , "in" : 데이터를 전체 가져와서 그리드 내부에서 페이징 및 편집
        index: true, // 그리드 번호  index : true ( asc )  , index : "DESC" ( desc 역순 )
        edit: false, // 그리드 편집 가능 여부 , default : false
        height: 500,
    },
    head: [
        { cells: [{ header: "L_BLL_NO", binding: "bllNo", width: 180 }] },
        { cells: [{ header: "L_BLL_DT", binding: "bllDt", width: 100 }] },
        { cells: [{ header: "L_TXPR", binding: "pyerMgmtNo", width: 120 }] },
        { cells: [{ header: "L_BLL_AMT", binding: "feeAmt", width: "*" }] },
        { cells: [{ header: "L_DUE_DT", binding: "payDdlnDt", width: 100 }] },
        { cells: [{ header: "L_DUE_DT_XTNS", binding: "ddlnXtnsDt", width: 100 }] },
        { cells: [{ header: "L_BLL_MGMT_ID", binding: "bllMgmtId", width: 180 }] },
        { cells: [{ header: "L_REGST_NM", binding: "xtnsRgsrOfcrNm", width: "*" }] },
        { cells: [{ header: "L_RGSR_DT", binding: "xtnsRgsrDt", width: 100 }] },
        // { cells: [{ header: "L_APRV_STAT_CD", binding: "aprvPrcssStatCd", width: "*" }] },
        { cells: [{ header: "L_APRV_STAT_CD", binding: "ddlnXtnsStatCd", width: "2*" }] },
    ],
    body: [
        { cells: [{ binding: "bllNo" }] },
        { cells: [{ binding: "bllDt", type: "date" }] },
        { cells: [{ binding: "pyerMgmtNo", mask: colUtils.getMaskPattern("tin") }] },
        { cells: [{ binding: "feeAmt", type: "number", thousandSeparator: true, align: "right" }] },
        { cells: [{ binding: "payDdlnDt", type: "date" }] },
        { cells: [{ binding: "ddlnXtnsDt", type: "date" }] },
        { cells: [{ binding: "bllMgmtId", mask: colUtils.getMaskPattern("bllNo") }] },
        { cells: [{ binding: "xtnsRgsrOfcrNm" }] },
        { cells: [{ binding: "xtnsRgsrDt", type: "date" }] },
        // { cells: [{ binding: "aprvPrcssStatCd", type: "code", area: "comnCd", comnCd: "OSC0096" }] },
        { cells: [{ binding: "ddlnXtnsStatCd", type: "code", area: "comnCd", comnCd: "OSC0253" }] },
    ],
};

/*
 * @ Form 스키마 정의
 * @ 화면에서 사용하는 Form 구조 정의
 * @ SF_ 로 시작 (Schema form)
 * @ SF_[Form 이름 메타 대문자, "_" 로 구분 ]
 * @ 검색폼은 _SRCH 로 구분
 */

// Schema of Bill List Search Form !== 고지 목록 검색 폼 스키마 ==!
export const SF_BLL_LIST_SRCH: TFormSchema = {
    id: "form_bllListSrch",
    schema: {
        cstmOfceCd: {
            type: "code",
            label: "L_CSTM_OFCE",
            area: "cstmCd",
            popupSize: "md",
        },
        pyerMgmtNo: {
            type: "text",
            label: "L_TXPR_TIN",
            mask: colUtils.getMaskPattern("tin"),
        },
        appNo: {
            type: "text",
            label: "L_APP_NO",
            edit: true,
        } /* Application No !== 신청서 번호 ==!*/,
        bllNo: {
            type: "text",
            label: "L_BLL_NO",
            letterCase: "upper",
        },
        bllMgmtId: {
            type: "text",
            label: "L_BLL_MGMT_ID",
            letterCase: "upper",
            mask: colUtils.getMaskPattern("bllNo"),
        },
        bllDtmRnge: {
            type: "daterange",
            label: "L_BLL_DT",
            start: {
                name: "bllStrtDt",
                defaultValue: comnUtils.getDate({ d: -7 }),
                required: true,
            },
            end: { name: "bllEndDt", defaultValue: comnUtils.getDate(), required: true },
            rangeButton: 2,
            controlSize: 10,
            required: true,
        },
        tansadNo: {
            type: "text",
            label: "L_TANSAD_NO",
            letterCase: "upper",
            mask: colUtils.getMaskPattern("tansadNo"),
        },
        tansadDtmRnge: {
            type: "daterange",
            label: "L_TANSAD_DT",
            start: { name: "tansadStrtDt" },
            end: { name: "tansadEndDt" },
            rangeButton: 2,
            controlSize: 10,
        },
        ctrlNo: {
            type: "text",
            label: "L_CTRL_NO",
        },
        bllStatCd: {
            type: "select",
            label: "L_BLL_STAT",
            area: "comnCd",
            comnCd: "OSC0056",
            all: true,
        },
        oilDclrYn: {
            type: "text",
        },
    },
};

// Schema of Bill Search Form !== 고지 검색 폼 스키마 ==!
export const SF_BLL_SRCH: TFormSchema = {
    id: "form_bllSrch",
    schema: {
        bllNo: {
            label: "L_BLL_NO",
        },
        bllSysmCd: {
            type: "text",
            minLength: 4,
            maxLength: 4,
            required: true,
            size: 2,
            mask: [/[A-Z0-9]/, /[A-Z0-9]/, /[A-Z0-9]/, /[A-Z0-9]/],
        },
        bllYy: {
            type: "text",
            minLength: 2,
            maxLength: 2,
            required: true,
            size: 1,
            mask: [/\d/, /\d/],
        },
        bllTpCd: {
            type: "text",
            minLength: 1,
            maxLength: 1,
            required: true,
            size: 1,
            mask: [/[A-Z]/],
        },
        bllSrno: {
            type: "text",
            minLength: 7,
            maxLength: 7,
            required: true,
            size: 2,
            mask: [/[A-Z]/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/],
        },
        bllDgr: {
            type: "text",
            minLength: 1,
            maxLength: 2,
            required: true,
            size: 1,
            mask: [/\d/, /\d/],
        },
    },
};

// Schema of TANSAD Search Form !== TANSAD 검색 폼 스키마 ==!
export const SF_TANSAD_SRCH: TFormSchema = {
    id: "form_tansadSrch",
    schema: {
        tansadNo: {
            label: "L_TANSAD_NO",
        },
        cstmOfceCd: {
            type: "text",
            minLength: 4,
            maxLength: 4,
            required: true,
            size: 2,
            mask: [/[A-Z]/, /[A-Z]/, /[A-Z0-9]/, /[A-Z0-9]/],
        },
        tansadYy: {
            type: "text",
            minLength: 2,
            maxLength: 2,
            required: true,
            size: 1,
            mask: [/\d/, /\d/],
        },
        tansadSrno: {
            type: "text",
            minLength: 7,
            maxLength: 7,
            required: true,
            size: 2,
            mask: [/\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/],
        },
    },
};

// Schema of Bill Detail Form !== 고지 상세 폼 스키마 ==!
export const SF_BLL_DTL: TFormSchema = {
    id: "form_bllDtl",
    schema: {
        // TANSAD General
        tansadNo: {
            type: "text",
            label: "L_TANSAD_NO",
            mask: colUtils.getMaskPattern("tansadNo"),
        },
        tansadDt: {
            type: "datetime",
            label: "L_TANSAD_DT",
        },
        regm: {
            type: "code",
            label: "L_REGM",
            area: "comnCd",
            comnCd: "OSC0072",
        },
        csclPlnCd: {
            type: "code",
            label: "L_CSCL_PLN",
            area: "comnCd",
            comnCd: "OSC0023",
        },
        exppnImppnTinNm: {
            type: "text",
            label: "L_EXPPN_IMPPN",
        },
        dcltTinNm: {
            type: "text",
            label: "L_DCLT_TIN",
        },
        dclrPrcssSttsCd: {
            type: "code",
            label: "L_PRCSS_STAT_CD",
            area: "comnCd",
            comnCd: "OSC0018",
        },
        // Bill General
        bllNo: {
            type: "text",
            label: "L_BLL_NO",
            mask: colUtils.getMaskPattern("bllNo"),
        },
        bllCstmOfceCd: {
            type: "text",
        },
        bllYy: {
            type: "text",
        },
        bllTpCd: {
            type: "text",
        },
        bllSrno: {
            type: "text",
        },
        bllDgr: {
            type: "text",
        },
        bllDt: {
            type: "datetime",
            label: "L_BLL_DT",
        },
        ctrlNo: {
            type: "text",
            label: "L_CTRL_NO",
        },
        zmpDt: {
            type: "datetime",
            label: "L_ZMP_DT",
        },
        bllTpCdSec: {
            type: "code",
            label: "L_BLL_TP",
            area: "comnCd",
            comnCd: "OSC0058",
        },
        bllStatCd: {
            type: "code",
            label: "L_BLL_STAT",
            area: "comnCd",
            comnCd: "OSC0056",
        },
        pyerMgmtNoNm: {
            type: "text",
            label: "L_TXPR_TIN",
        },
        reffKey: {
            type: "text",
            label: "L_REFF_KEY",
        },
        reffKeyTpCd: {
            type: "code",
            label: "L_REFF_TP",
            area: "comnCd",
            comnCd: "OSC0067",
        },
        bllRgsrIdNm: {
            type: "text",
            label: "L_RGSR",
        },
        dtyAmt: {
            type: "number",
            label: "L_DTY_AMT",
        },
        pnltAmt: {
            type: "number",
            label: "L_PNLT_AMT",
        },
        vatAmt: {
            type: "number",
            label: "L_VAT_AMT",
        },
        feeAmt: {
            type: "number",
            label: "L_FEE_AMT",
        },
        othrAmt: {
            type: "number",
            label: "L_OTHR_AMT",
        },
        totBllTaxAmt: {
            type: "number",
            label: "L_TOT_BLL_TAX_AMT",
        },
        payDdlnDt: {
            type: "date",
            label: "L_DDLN_DT",
        },
        lastDdlnXtnsDt: {
            type: "date",
            label: "L_DDLN_XTNS_DT",
        },
        xtnsDt: {
            type: "number",
            label: "L_XTNS_DD",
        },
        lastDdlnXtnsRsn: {
            type: "text",
            label: "L_DDLN_XTNS_RSN",
        },
        // Bill Cancel
        dspoDt: {
            type: "date",
            label: "L_CNCL_DT",
        },
        dspoCd: {
            type: "code",
            label: "L_CNCL_CD",
            area: "comnCd",
            comnCd: "OSC0057",
        },
        dspoRmrk: {
            type: "text",
            label: "L_CNCL_RSN",
            controlSize: 10,
        },
        // Bill Receipt
        payMetdCd: {
            type: "code",
            label: "L_PAY_METD",
            area: "comnCd",
            comnCd: "OSC0065",
        },
        trsyVchrNo: {
            type: "text",
            label: "L_TRSY_VCHR_NO",
        },
        rcptBnkCd: {
            type: "code",
            label: "L_RCPT_BNK",
            area: "bnkCd",
        },
        rcptBnkAcctNo: {
            type: "text",
            label: "L_RCPT_BNK_ACCT_NO",
        },
        rcptNo: {
            type: "text",
            label: "L_RCPT_NO",
        },
        manlRcptRsn: {
            type: "text",
            label: "L_MANL_RCPT_RSN",
        },
        rcptAmt: {
            type: "number",
            label: "L_RCPT_AMT",
        },
        rcptDt: {
            type: "date",
            label: "L_RCPT_DT",
        },
    },
};

// Schema of Deadline Extension List Search Form !== 데드라인 연장 목록 검색 폼 스키마 ==!
export const SF_DDLN_XTNS_LIST_SRCH: TFormSchema = {
    id: "form_ddlnXtnsListSrch",
    schema: {
        bllNo: {
            type: "text",
            label: "L_BLL_NO",
        },
        bllMgmtId: {
            type: "text",
            label: "L_BLL_MGMT_ID",
            letterCase: "upper",
            mask: colUtils.getMaskPattern("bllNo"),
        },
        pyerMgmtNo: {
            type: "text",
            label: "L_TXPR_TIN",
            mask: colUtils.getMaskPattern("tin"),
        },
        xtnsRgsrDtmRnge: {
            type: "daterange",
            label: "L_XTNS_RGSR_DT",
            start: { name: "strtDt", defaultValue: comnUtils.getDate({ m: -3 }), required: true },
            end: { name: "endDt", defaultValue: comnUtils.getDate(), required: true },
            rangeButton: 0,
            controlSize: 10,
            required: true,
        },
        ddlnXtnsStatCd: {
            type: "select",
            label: "L_APRV_STAT_CD",
            area: "comnCd",
            comnCd: "OSC0253",
            all: true,
        },
    },
};

// Schema of Deadline Extension Detail Form !== 데드라인 연장 상세 폼 스키마 ==!
export const SF_DDLN_XTNS_DTL: TFormSchema = {
    id: "form_ddlnXtnsDtl",
    schema: {
        bllNo: {
            type: "text",
        },
        bllTpCd: {
            type: "text",
        },
        bllSysmCd: {
            type: "text",
        },
        bllYy: {
            type: "text",
        },
        bllSrno: {
            type: "text",
        },
        bllDgr: {
            type: "text",
        },
        ddlnXtnsDt: {
            type: "date",
            label: "L_DUE_DT_XTNS",
        },
        ddlnXtnsRsn: {
            type: "text",
            label: "L_XTNS_RSN",
        },
        xtnsRgsrOfcrNm: {
            type: "text",
            label: "L_REGST_NM",
        },
        aprvPrcssStatCd: {
            type: "select",
            label: "L_APRV_STAT_CD",
            area: "comnCd",
            comnCd: "OSC0096",
        },
        ddlnXtnsStatCd: {
            type: "select",
            label: "L_APRV_STAT_CD",
            area: "comnCd",
            comnCd: "OSC0253",
        },
    },
};

// Schema of Bill List Grid !== 고지 목록 그리드 스키마 ==!
export const SG_BLL_DTL_LIST: TGridSchema = {
    id: "grid_blldDtlList",
    options: {
        index: true, // 그리드 번호  index : true ( asc )  , index : "DESC" ( desc 역순 )
        edit: false, // 그리드 편집 가능 여부 , default : false
        height: "auto",
    },
    head: [
        { cells: [{ header: "L_BLL_NO", binding: "bllNo", width: 180 }] },
        { cells: [{ header: "L_APP_NO", binding: "appNo", width: 200 }] },
        { cells: [{ header: "L_BLL_MGMT_ID", binding: "bllMgmtId", width: 180 }] },
        { cells: [{ header: "L_BLL_DT", binding: "bllDt", width: 100 }] },
        { cells: [{ header: "L_CTRL_NO", binding: "ctrlNo", width: "*" }] },
        { cells: [{ header: "L_BLL_AMT", binding: "feeAmt", width: "*" }] },
        { cells: [{ header: "L_RCPT_AMT", binding: "rcptAmt", width: "*" }] },
        { cells: [{ header: "L_DUE_DT", binding: "lastPayDdlnDt", width: 100 }] },
        { cells: [{ header: "L_CNCL_YN", binding: "dspoYn", width: 80 }] },
    ],
    body: [
        { cells: [{ binding: "bllNo" }] },
        { cells: [{ binding: "appNo" }] },
        { cells: [{ binding: "bllMgmtId" }] },
        { cells: [{ binding: "bllDt", type: "date" }] },
        { cells: [{ binding: "ctrlNo" }] },
        { cells: [{ binding: "feeAmt", type: "number", thousandSeparator: true, align: "right" }] },
        { cells: [{ binding: "rcptAmt", type: "number", thousandSeparator: true, align: "right" }] },
        { cells: [{ binding: "lastPayDdlnDt", type: "date" }] },
        { cells: [{ binding: "dspoYn" }] },
    ],
};

// Schema of Bill List Grid !== 고지 목록 그리드 스키마 ==!
export const SG_UPD_BLL_DTL_LIST: TGridSchema = {
    id: "grid_blldDtlList",
    options: {
        index: true, // 그리드 번호  index : true ( asc )  , index : "DESC" ( desc 역순 )
        edit: false, // 그리드 편집 가능 여부 , default : false
        height: "auto",
    },
    head: [
        { cells: [{ header: "L_BLL_NO", binding: "bllNo", width: 180 }] },
        { cells: [{ header: "L_APP_NO", binding: "appNo", width: 200 }] },
        { cells: [{ header: "L_BLL_MGMT_ID", binding: "bllMgmtId", width: 180 }] },
        { cells: [{ header: "L_BLL_DT", binding: "bllDt", width: 100 }] },
        { cells: [{ header: "L_CTRL_NO", binding: "ctrlNo", width: "*" }] },
        { cells: [{ header: "L_BLL_AMT", binding: "feeAmt", width: "*" }] },
        { cells: [{ header: "L_DUE_DT", binding: "lastPayDdlnDt", width: 100 }] },
    ],
    body: [
        { cells: [{ binding: "bllNo" }] },
        { cells: [{ binding: "appNo" }] },
        { cells: [{ binding: "bllMgmtId" }] },
        { cells: [{ binding: "bllDt", type: "date" }] },
        { cells: [{ binding: "ctrlNo" }] },
        { cells: [{ binding: "feeAmt", type: "number", thousandSeparator: true, align: "right" }] },
        { cells: [{ binding: "lastPayDdlnDt", type: "date" }] },
    ],
};

// Schema of Bill List Grid !== 고지 목록 그리드 스키마 ==!
export const SG_BLL_DTLS: TGridSchema = {
    id: "grid_blldDtlList",
    options: {
        index: true, // 그리드 번호  index : true ( asc )  , index : "DESC" ( desc 역순 )
        edit: false, // 그리드 편집 가능 여부 , default : false
        height: "auto",
    },
    head: [
        { cells: [{ header: "L_BLL_NO", binding: "bllNo", width: 180 }] },
        { cells: [{ header: "L_BLL_MGMT_ID", binding: "bllMgmtId", width: 180 }] },
        { cells: [{ header: "L_OGA", binding: "orgAbrtNm", width: 80 }] },
        { cells: [{ header: "L_APP", binding: "appNm", width: "*" }] },
        { cells: [{ header: "L_APP_NO", binding: "appNo", width: 200 }] },
        { cells: [{ header: "L_CTRL_NO", binding: "ctrlNo", width: "*" }] },
        { cells: [{ header: "L_BLL_AMT", binding: "feeAmt", width: "*" }] },
        { cells: [{ header: "L_RCPT_AMT", binding: "rcptAmt", width: "*" }] },
    ],
    body: [
        { cells: [{ binding: "bllNo" }] },
        { cells: [{ binding: "bllMgmtId" }] },
        { cells: [{ binding: "orgAbrtNm" }] },
        { cells: [{ binding: "appNm", align: "left" }] },
        { cells: [{ binding: "appNo" }] },
        { cells: [{ binding: "ctrlNo" }] },
        { cells: [{ binding: "feeAmt", type: "number", thousandSeparator: true, align: "right" }] },
        { cells: [{ binding: "rcptAmt", type: "number", thousandSeparator: true, align: "right" }] },
    ],
};

export const SG_BLL_ITM_LIST: TGridSchema = {
    id: "grid_blldDtlList",
    options: {
        index: true, // 그리드 번호  index : true ( asc )  , index : "DESC" ( desc 역순 )
        edit: false, // 그리드 편집 가능 여부 , default : false
        height: "auto",
    },
    head: [
        { cells: [{ header: "L_BLL_NO", binding: "bllNo", width: "*" }] },
        { cells: [{ header: "L_BLL_MGMT_ID", binding: "bllMgmtId", width: "*" }] },
        { cells: [{ header: "L_GFS_CD", binding: "gfsCd", width: "*" }] },
        { cells: [{ header: "L_ITM_NM", binding: "itmNm", width: "2*" }] },
        { cells: [{ header: "L_AMT", binding: "bllItmAmt", width: "*" }] },
        { cells: [{ header: "L_EQV_AMT", binding: "bllItmAmt", width: "*" }] },
    ],
    body: [
        { cells: [{ binding: "bllNo" }] },
        { cells: [{ binding: "bllMgmtId", mask: colUtils.getMaskPattern("bllNo") }] },
        { cells: [{ binding: "gfsCd" }] },
        { cells: [{ binding: "itmNm", align: "left" }] },
        { cells: [{ binding: "bllItmAmt", type: "number", thousandSeparator: true, align: "right" }] },
        {
            cells: [{ binding: "bllItmTzsAmt", type: "number", thousandSeparator: true, align: "right" }],
        },
    ],
};
